/***
 * Add to cart form component
 * 
 * 用法1：直接加购（单变体或已知 variant ID）
 * <add-to-cart-from
 *   class="btn"
 *   data-product-info='{"id":"{{ variant.id }}", "quantity":"1"}'
 * >
 *   <span class="add__cart-text">Add to cart</span>
 *   <div class="loading-overlay__spinner hidden">
 *     {%- render 'icon-cart-loading' -%}
 *   </div>
 * </add-to-cart-from>
 * 
 * 用法2：打开选择弹窗（多变体产品）
 * <add-to-cart-from
 *   class="btn"
 *   data-product-url="{{ product.url }}"
 * >
 *   <span class="add__cart-text">Quick Add</span>
 *   <div class="loading-overlay__spinner hidden">
 *     {%- render 'icon-cart-loading' -%}
 *   </div>
 * </add-to-cart-from>
 * 
 * 属性说明：
 * - data-product-info: 商品变体信息（JSON格式），包含 id 和 quantity
 * - data-product-url: 商品页面 URL，用于获取完整的变体选择界面
 * - 两个属性二选一，优先使用 data-product-info
 */

class AddToCartFrom extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    // 组件本身就是按钮，不需要再查找子元素
    // 初始化各种必要的属性
    this.initProperties();
    // 准备请求数据（只有在有 productInfo 时才准备）
    if (this.productInfo) {
      this.prepareRequestData();
    }
    // 添加事件监听器
    this.addEventListener('click', this.handleAddToCartClick.bind(this));
  }

  /**
   * 初始化组件属性
   */
  initProperties() {
    this.addCartText = this.querySelector('.add__cart-text');
    this.loadingSpinner = this.querySelector('.loading-overlay__spinner');
    this.cartDrawer = document.querySelector('cart-drawer');
    this.productInfo = this.getAttribute('data-product-info');
    this.productUrl = this.getAttribute('data-product-url');
    this.cartAddUrl = window.routes?.cart_add_url || '/cart/add.js';

    // 只有在有 productInfo 时才格式化商品数据
    if (this.productInfo) {
      this.formattedItems = this.formatProductItems();
    }
  }



  /**
   * 格式化商品数据
   * @returns {Array} 格式化后的商品数据
   */
  formatProductItems() {
    try {
      let productItems = Array.isArray(this.productInfo) ?
        JSON.parse(this.productInfo) : [JSON.parse(this.productInfo)];

      return productItems.map(item => ({
        quantity: item.quantity || 1,
        id: parseInt(item.variantId || item.id)
      }));
    } catch (error) {
      console.error('商品数据格式化失败:', error);
      return [];
    }
  }

  /**
   * 获取fetchConfig配置
   * @param {string} type - 请求类型
   * @returns {Object} 请求配置对象
   */
  getFetchConfig(type = 'json') {
    if (typeof window.fetchConfig === 'function') {
      return window.fetchConfig(type);
    }
    return {
      method: 'POST',
      headers: {
        'Content-Type': type === 'json' ? 'application/json' : 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    };
  }

  /**
   * 准备请求数据
   */
  prepareRequestData() {
    // 构建基础配置
    this.config = this.getFetchConfig('json');

    // 准备请求数据
    let requestData = {
      items: this.formattedItems
    };

    // 添加sections数据（如果有购物车抽屉）
    this.addSectionsData(requestData);

    // 转换为JSON
    this.config.body = JSON.stringify(requestData);
  }

  /**
   * 添加购物车sections数据
   * @param {Object} requestData - 请求数据对象
   */
  addSectionsData(requestData) {
    if (!this.cartDrawer || typeof this.cartDrawer.getSectionsToRender !== 'function') {
      return;
    }

    try {
      const sections = this.cartDrawer.getSectionsToRender().map(
        section => section.id || section.section || section
      );
      requestData.sections = sections;
      requestData.sections_url = window.location.pathname;
    } catch (error) {
      console.error('Error getting sections:', error);
    }
  }

  /**
   * 处理添加到购物车点击事件
   * @param {Event} event - 点击事件
   */
  handleAddToCartClick(event) {
    event.preventDefault();
    event.stopPropagation();

    try {
      // 判断是直接加购还是打开选择弹窗
      if (this.productInfo) {
        // 有 variant ID，直接加购
        this.showLoadingState();
        this.addToCart();
      } else if (this.productUrl) {
        // 只有 product URL，打开选择弹窗
        this.openQuickAddDrawer();
      } else {
        console.error('需要提供 data-product-info 或 data-product-url');
        this.showErrorMessage('缺少必要的产品信息');
      }
    } catch (error) {
      console.error('Error processing add to cart:', error);
      this.hideLoadingState();
      this.showErrorMessage(error.message);
    }
  }

  /**
   * 显示加载状态
   */
  showLoadingState() {
    if (this.addCartText) this.addCartText.classList.add('hidden');
    if (this.loadingSpinner) this.loadingSpinner.classList.remove('hidden');
  }

  /**
   * 隐藏加载状态
   */
  hideLoadingState() {
    if (this.addCartText) this.addCartText.classList.remove('hidden');
    if (this.loadingSpinner) this.loadingSpinner.classList.add('hidden');
  }

  /**
   * 添加商品到购物车
   */
  addToCart() {
    fetch(this.cartAddUrl, this.config)
      .then(this.handleResponse.bind(this))
      .then(this.handleSuccess.bind(this))
      .catch(this.handleError.bind(this))
      .finally(this.ensureCartNotEmpty.bind(this));
  }

  /**
   * 处理API响应
   * @param {Response} response - fetch请求响应
   * @returns {Promise} 解析的JSON数据
   */
  async handleResponse(response) {
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || `服务器返回错误: ${response.status}`);
    }
    return response.json();
  }

  /**
   * 处理成功添加到购物车
   * @param {Object} data - 响应数据
   */
  handleSuccess(data) {
    console.log('Items added successfully:', data);
    this.hideLoadingState();
    this.updateCartDrawer(data);
    this.showSuccessMessage();
  }

  /**
   * 更新购物车抽屉
   * @param {Object} data - 购物车数据
   */
  updateCartDrawer(data) {
    if (!this.cartDrawer) return;

    // 更新购物车内容
    if (typeof this.cartDrawer.renderContents === 'function') {
      this.cartDrawer.renderContents(data);
    }

    // 打开购物车抽屉
    this.cartDrawer.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
  }

  /**
   * 显示成功消息
   */
  showSuccessMessage() {
    if (window.pushSuccessMessage) {
      window.pushSuccessMessage(window.cartStrings?.success || '商品已添加到购物车');
    }
  }

  /**
   * 处理错误
   * @param {Error} error - 错误对象
   */
  handleError(error) {
    console.error('Error adding items to cart:', error);
    this.hideLoadingState();
    this.showErrorMessage(error.message);
  }

  /**
   * 显示错误消息
   * @param {string} message - 错误消息
   */
  showErrorMessage(message) {
    if (window.pushErrorMessage) {
      window.pushErrorMessage(message || '添加商品失败，请稍后重试');
    }
  }

  /**
   * 确保购物车不显示为空
   */
  ensureCartNotEmpty() {
    if (this.cartDrawer && this.cartDrawer.classList.contains('is-empty')) {
      this.cartDrawer.classList.remove('is-empty');
    }
  }

  /**
   * 打开快速添加弹窗（用于多变体产品）
   */
  openQuickAddDrawer() {
    this.showLoadingState();

    // 构建 URL，添加 section_id 参数
    let url = this.productUrl;
    if (url.includes('?')) {
      url += '&';
    } else {
      url += '?';
    }
    url += 'section_id=quick-add-drawer';

    // Fetch 产品页面内容
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        // 调试：查看返回的 HTML
        console.log('Response HTML:', responseText.substring(0, 500));

        // 使用全局的 getDomHtmlFromText 函数
        const newQuickAddDrawer = getDomHtmlFromText(responseText, 'quick-add-drawer');

        // 添加空值检查
        if (!newQuickAddDrawer) {
          throw new Error('未找到 quick-add-drawer 元素');
        }

        if (window.hasQuickAddDrawer) {
          // 已存在弹窗，更新内容
          const quickAddDrawer = document.querySelector('quick-add-drawer');
          const mainProduct = newQuickAddDrawer.querySelector('.quick-add-main-product');
          if (quickAddDrawer && mainProduct) {
            quickAddDrawer.setInnerHTML(mainProduct.innerHTML);
            quickAddDrawer.open(this);
          }
        } else {
          // 首次创建弹窗
          document.body.appendChild(newQuickAddDrawer);
          newQuickAddDrawer.reInjectScript(newQuickAddDrawer);
          setTimeout(() => {
            newQuickAddDrawer.open(this);
          }, 300);
          window.hasQuickAddDrawer = true;
        }
      })
      .catch((error) => {
        console.error('Error loading product details:', error);
        this.showErrorMessage('加载产品详情失败');
      })
      .finally(() => {
        this.hideLoadingState();
      });
  }
}

customElements.define('add-to-cart-from', AddToCartFrom);
